import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useVoyagerStore from '../stores/useVoyagerStore';
import { openDraftInStudio } from '../components/Connections/introspection';
import { useSimpleSidebar } from './simpleSidebarState';
import { SOURCE_LABEL, useActionItems, type ActionItem, type ActionItemSource, type ActionTarget } from './actionItems';

/** Opens the place a row's decision is made; the decision there keeps its own permission check. */
export function openActionItem(target: ActionTarget) {
  const s = useVoyagerStore.getState();
  useSimpleSidebar.getState().showPlaceholder(null);
  switch (target.to) {
    case 'review': case 'thread': s.loadThread(target.threadId); break;
    case 'activity': s.navigateToActivity(target.agentId || undefined); break;
    case 'wall': s.navigateToAgentWall(target.agentId); break;
    case 'plan': s.navigateToObjective(target.agentId, 'plan'); break;
    case 'usage': s.navigateToObjective(target.agentId, 'usage'); break;
    // Knowledge › Needs review is where a colleague's draft, a lesson, a proposed link and an upload are decided. With the
    // flag on its tab is off Knowledge's nav (#1511), but its address, /knowledge?tab=review, keeps opening it; your own
    // draft (Needs review lists only colleagues') is published in Definitions.
    case 'definition': s.navigateToBrain(target.mine ? 'definitions' : 'review'); break;
    case 'definitions': case 'lessons': case 'knowledge-review': s.navigateToBrain('review'); break;
    case 'draft': void openDraftInStudio(target.draftId); break;
  }
}

const ORDER: ActionItemSource[] = ['home', 'definition', 'model', 'knowledge'];
const FOCUS = { '&:focus-visible': { outline: '2px solid var(--acc)', outlineOffset: -2 } } as const;

function Row({ item }: { item: ActionItem }) {
  return <Box component="button" type="button" data-testid="action-item" data-source={item.source} onClick={() => openActionItem(item.target)}
    aria-label={`${item.action}: ${item.title}`}
    sx={{ appearance: 'none', border: 0, borderBottom: '1px solid var(--line-2)', bgcolor: 'transparent', width: '100%', display: 'grid',
      gridTemplateColumns: 'auto minmax(0,1fr) auto', gap: 1.5, alignItems: 'center', px: 2, py: 1.5, textAlign: 'left', font: 'inherit',
      color: 'var(--ink)', cursor: 'pointer', '&:last-of-type': { borderBottom: 0 }, '&:hover': { bgcolor: 'var(--sunk)' }, ...FOCUS }}>
    <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'var(--run)' }} />
    <Box component="span" sx={{ minWidth: 0 }}>
      <Box component="span" sx={{ display: 'block', fontSize: 13.5, overflowWrap: 'anywhere' }}>{item.title}</Box>
      {item.detail && <Box component="small" sx={{ display: 'block', color: 'var(--ink-3)', fontSize: 12, overflowWrap: 'anywhere' }}>{item.detail}</Box>}
    </Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 12.5, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
      {item.action}<ChevronRightIcon sx={{ fontSize: 16, color: 'var(--ink-3)' }} />
    </Box>
  </Box>;
}

/** Action items (#1509), the simplified sidebar's page. */
export default function ActionItemsPage() {
  const { rows, notes, loaded, load } = useActionItems();
  React.useEffect(() => { void load(); }, [load]);
  const groups = ORDER.map((source) => ({ source, items: rows.filter((r) => r.source === source) })).filter((g) => g.items.length);
  return <Box data-testid="action-items-page" sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'var(--bg)' }}>
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 4 }, pt: 4, pb: 6, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.01em', mb: 0.5 }}>Action items</Typography>
        <Typography sx={{ color: 'var(--ink-3)', fontSize: 13.5, maxWidth: '70ch' }}>
          Decisions that need a person before Stroma can act on them. Your analysis never waits on these.
        </Typography>
      </Box>
      {groups.map((g) => <Box key={g.source} component="section" aria-label={SOURCE_LABEL[g.source]} data-testid={`action-items-${g.source}`}>
        <Box sx={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-2)', mb: 1, display: 'flex', gap: 1 }}>
          {SOURCE_LABEL[g.source]}<Box component="span" sx={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)' }}>{g.items.length}</Box>
        </Box>
        <Box sx={{ border: '1px solid var(--line)', borderRadius: '14px', overflow: 'hidden', bgcolor: 'var(--bg)' }}>
          {g.items.map((item) => <Row key={item.key} item={item} />)}
        </Box>
      </Box>)}
      {notes.length > 0 && <Typography role="status" data-testid="action-items-notes" sx={{ color: 'var(--ink-3)', fontSize: 12.5 }}>
        Some queues could not be read, so this list may be incomplete: {notes.join(' ')}
      </Typography>}
      {loaded && !rows.length && !notes.length && <Box data-testid="action-items-empty"
        sx={{ border: '1px solid var(--line)', borderRadius: '14px', px: 2, py: 1.75, color: 'var(--ink-3)', fontSize: 13.5 }}>
        Nothing is waiting on you.
      </Box>}
    </Box>
  </Box>;
}
